/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { Levels } from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelSectionHeadingData,
    IFieldDefinitionPanelSectionHeadingState
} from ".";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors, IFieldChangeData } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

/**
 * Commands for Section Heading field definition panel.
 */
export class FieldDefinitionPanelSectionHeadingCommands extends BaseCommands<
    IFieldDefinitionPanelSectionHeadingData,
    IFieldDefinitionPanelSectionHeadingState
> {
    /**
     * Constructor
     */
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd<IFieldDefinitionPanelSectionHeadingState, string>(
                this.OnBlurHandler
            ),
            onTextChanged: cb.bindCmd<IFieldDefinitionPanelSectionHeadingState, IFieldChangeData>(
                this.OnTextChangedHandler
            ),
            onAccessLevelChanged: cb.bindCmd<IFieldDefinitionPanelSectionHeadingState, Levels>(
                this.OnAccessLevelChangedHandler
            )
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelSectionHeadingState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Gets the panel data that needs to be save.
     *
     * @param state State containing panel data
     * @returns Data to save
     */
    Save = (
        state: IFieldDefinitionPanelSectionHeadingState
    ): IFieldDefinitionPanelSectionHeadingData => {
        logger.logInformation("Saving Section properties");

        // important to make a copy!
        return { ...state.data };
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(
        state: IFieldDefinitionPanelSectionHeadingState
    ): FormErrors<IFieldDefinitionPanelSectionHeadingData> {
        const errors: FormErrors<IFieldDefinitionPanelSectionHeadingData> = {};

        if (state.data.sectionHeadingId === "") {
            errors.sectionHeadingId = "Section Id is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Section id validation function not hooked up");
                throw new Error("Unable to validate Section id");
            }

            errors.sectionHeadingId = state.common.validateSettingName(
                state.data.sectionHeadingId,
                state.common.originalSettingName
            );
        }

        if (state.data.heading === "") {
            errors.heading = "Section is required.";
        }

        return errors;
    }
}
